import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import orderServices from "../../services/orders/orderservice";

interface Props {
  orderId: number;
  customerName: string;
  eligible: boolean;
  notify: (label: string, message: string, severity: "success" | "error" | "warn") => void;
  onEligibilityChanged: (orderId: number) => void;
}

export default function OrderPaymentLinkButton({ orderId, customerName, eligible, notify, onEligibilityChanged }: Props) {
  const [visible, setVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const send = async () => {
    if (!eligible || inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    try {
      const result = await orderServices.sendOpenOrderPaymentLink(orderId);
      if (result.isSuccess) {
        notify("Payment link", result.message || "Payment link requested for the customer's registered mobile/email.", "success");
      } else {
        notify("Payment link", result.message || "This order is no longer eligible for a payment link.", "warn");
      }
      // A stale row or a successful send must be refreshed before another attempt.
      onEligibilityChanged(orderId);
      setVisible(false);
    } catch {
      notify("Payment link", "Unable to confirm delivery. Check payment status before retrying.", "error");
      onEligibilityChanged(orderId);
      setVisible(false);
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  if (!eligible) return null;
  return <>
    <Button icon="pi pi-link" className="p-button-rounded p-button-text"
      style={{ border: "none", color: "#008c42", background: "none" }}
      tooltip="Send payment link" aria-label={`Send payment link for order ${orderId}`}
      disabled={sending} onClick={() => setVisible(true)} />
    <Dialog header="Send payment link" visible={visible} modal style={{ width: "28rem", maxWidth: "95vw" }}
      onHide={() => { if (!sending) setVisible(false); }} closable={!sending}
      footer={<>
        <Button label="Cancel" text disabled={sending} onClick={() => setVisible(false)} />
        <Button label="Send SMS/email" icon="pi pi-send" loading={sending} disabled={sending} onClick={send} />
      </>}>
      <p>Send a payment link for Order #{orderId} to {customerName}'s registered mobile/email?</p>
      <p>The order and payment status will be checked again before sending.</p>
    </Dialog>
  </>;
}
